import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/Seo";
import PageLayout from "@/components/layout/PageLayout";
import EventCard from "@/components/ui/EventCard";
import { events } from "@/mocks/events";
import { site } from "@/mocks/site";

const eventsSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "事件地图", item: SITE_URL + "/events" },
      ],
    },
    {
      "@type": "CollectionPage",
      name: "事件地图 - 吃瓜路线",
      url: SITE_URL + "/events",
      description: "吃瓜路线事件地图总览：按栏目与关键词筛选，查看各公共话题的时间线、关键人物与观点谱。",
      isPartOf: { "@type": "WebSite", name: "吃瓜路线", url: SITE_URL },
    },
    {
      "@type": "ItemList",
      itemListElement: events.map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: e.title,
        url: SITE_URL + "/events/" + e.slug,
      })),
    },
  ],
};

export default function Events() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const q = params.get("q") || "";

  const list = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return events.filter((e) => {
      const inCat = cat === "all" || e.categorySlug === cat;
      if (!inCat) return false;
      if (!kw) return true;
      const hay = `${e.title} ${e.summary} ${e.tags.join(" ")} ${e.category}`.toLowerCase();
      return hay.includes(kw);
    });
  }, [cat, q]);

  const setCat = (c: string) => {
    const next = new URLSearchParams(params);
    if (c === "all") next.delete("cat");
    else next.set("cat", c);
    setParams(next, { replace: true });
  };

  const setKeyword = (v: string) => {
    const next = new URLSearchParams(params);
    if (v.trim()) next.set("q", v.trim());
    else next.delete("q");
    setParams(next, { replace: true });
  };

  const countOf = (slug: string) => events.filter((e) => e.categorySlug === slug).length;

  return (
    <PageLayout>
      <Seo
        title="事件地图总览 - 吃瓜路线"
        description="在吃瓜路线事件地图总览中，按栏目与关键词筛选公共话题，查看每条事件的时间线、关键人物、各方观点与「已证实 / 未证实」溯源清单，理性吃透复杂事件。"
        keywords="吃瓜路线事件地图,吃瓜路线瓜田分布,热点坐标,事件时间线,观点谱,信息溯源"
        path="/events"
        schema={eventsSchema}
      />
      {/* 面包屑 */}
      <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
        <ol className="mx-auto flex w-full max-w-7xl items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
          <li>
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
          </li>
          <li className="text-foreground-300">/</li>
          <li aria-current="page" className="font-medium text-foreground-700">
            事件地图
          </li>
        </ol>
      </nav>

      {/* 头部 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">
          EVENT MAP · 瓜田全集
        </p>
        <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">事件地图总览</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          每份事件地图都包含：可标注来源的<strong className="text-foreground-900">时间线</strong>、清晰的
          <strong className="text-foreground-900">关键人物</strong>与<strong className="text-foreground-900">各方观点</strong>，
          以及单独分层的<strong className="text-foreground-900">已证实 / 未证实</strong>清单。先选栏目，或用关键词检索。
        </p>
      </section>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 pb-14 md:px-6 lg:grid-cols-4">
        {/* 侧栏筛选 */}
        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 lg:sticky lg:top-20">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-filter-3-line text-lg text-accent-600" aria-hidden="true"></i>
              按栏目筛选
            </h2>
            <ul className="mt-4 space-y-1.5">
              <li>
                <button
                  type="button"
                  onClick={() => setCat("all")}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                    cat === "all"
                      ? "bg-primary-500 font-medium text-background-50"
                      : "text-foreground-700 hover:bg-background-50"
                  }`}
                >
                  <span>全部栏目</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      cat === "all" ? "bg-background-50/20" : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {events.length}
                  </span>
                </button>
              </li>
              {site.categories.map((c) => (
                <li key={c.slug}>
                  <button
                    type="button"
                    onClick={() => setCat(c.slug)}
                    className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                      cat === c.slug
                        ? "bg-primary-500 font-medium text-background-50"
                        : "text-foreground-700 hover:bg-background-50"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <i className={c.icon} aria-hidden="true"></i>
                      {c.name}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        cat === c.slug ? "bg-background-50/20" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {countOf(c.slug)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <h3 className="mt-6 flex items-center gap-2 text-sm font-semibold text-foreground-900">
              <i className="ri-flag-line text-accent-600" aria-hidden="true"></i>
              状态图例
            </h3>
            <ul className="mt-3 space-y-2 text-xs text-foreground-600">
              <li className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-primary-500"></span> 持续发酵 · 仍有新进展
              </li>
              <li className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-accent-400"></span> 基本落地 · 有明确结论
              </li>
              <li className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-secondary-400"></span> 热度回落 / 等待回应
              </li>
            </ul>
          </div>
        </aside>

        {/* 主列表 */}
        <div className="lg:col-span-3">
          {/* 搜索 */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const input = (e.currentTarget.elements.namedItem("q") as HTMLInputElement).value;
              setKeyword(input);
            }}
            className="mb-6 flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-1.5 focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100"
          >
            <span className="pl-3 text-foreground-400">
              <i className="ri-search-line text-lg" aria-hidden="true"></i>
            </span>
            <input
              name="q"
              defaultValue={q}
              key={q}
              type="search"
              placeholder="输入关键词，例如：国潮、AI、直播…"
              aria-label="按关键词检索事件地图"
              className="w-full bg-transparent py-2 text-sm outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              检索
            </button>
          </form>

          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共 <span className="font-semibold text-foreground-900">{list.length}</span> 份事件地图
              {cat !== "all" && ` · ${site.categories.find((c) => c.slug === cat)?.name || ""}`}
              {q && ` · 关键词「${q}」`}
            </p>
            {(cat !== "all" || q) && (
              <button
                type="button"
                onClick={() => {
                  setCat("all");
                  setKeyword("");
                }}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true"></i>
                清除筛选
              </button>
            )}
          </div>

          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <i className="ri-inbox-line text-4xl text-foreground-300" aria-hidden="true"></i>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-800">还没有匹配的事件地图</p>
              <p className="mt-1 text-sm text-foreground-500">换一个栏目或关键词试试，或查看全部。</p>
              <button
                type="button"
                onClick={() => {
                  setCat("all");
                  setKeyword("");
                }}
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                查看全部事件
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {list.map((e) => (
                <EventCard key={e.slug} event={e} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}